/**
 * OCR 识别状态管理
 * 管理识别请求、响应、参数等
 */
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

/**
 * @typedef {Object} ParamsItem
 * @property {string} key - 参数键
 * @property {string} label - 参数标签
 * @property {string} type - 参数类型
 * @property {any} default - 默认值
 */

/**
 * @typedef {Object} SmartKeyItem
 * @property {string} key - 唯一标识
 * @property {string} value - 值
 * @property {boolean} isCustom - 是否自定义
 * @property {string} type - 类型 'input' | 'select'
 * @property {string} status - 状态 '' | 'error'
 * @property {boolean} showClose - 是否显示关闭按钮
 */

/**
 * @typedef {Object} TabItem
 * @property {string} id - Tab ID
 * @property {string} label - Tab 标签
 * @property {string} type - Tab 类型
 */

const defaultRspTabs = [
  { id: 'Result', label: '识别结果', type: 'Result' },
  { id: 'Request', label: 'Request', type: 'Request' },
  { id: 'Response', label: 'Response', type: 'Response' }
];

const initialState = {
  // 参数相关
  paramsValue: {},          // 显示的参数值
  requestParamsValue: {},   // 实际请求参数值
  paramsList: [],           // 参数配置列表
  smartKeyList: [],         // 智能结构化字段列表
  
  // API 类型
  apiType: '',              // 'sync' | 'async' | ''
  
  // 请求响应
  request: {},              // 请求参数
  response: {},             // 完整响应（包含 auditData）
  finalResponse: {},        // 最终响应（不含 auditData）
  
  // Tab 相关
  rspTabs: defaultRspTabs,
  currentTab: 'Result',
  
  // 教育相关
  rectOptions: [],          // eduAgent 矩形选项
  eduMarkData: null,        // eduAgent 批改数据
  
  // 控制参数面板显示
  showParams: true,
};

export const useOcrStore = create(
  devtools(
    (set, get) => ({
      ...initialState,

      // ========== 参数操作 ==========

      /**
       * 设置参数值
       * @param {Object} params
       */
      setParamsValue: (params) => set({ paramsValue: params }, false, 'ocr/setParamsValue'),

      /**
       * 更新单个参数值
       * @param {string} key
       * @param {any} value
       */
      updateParamValue: (key, value) => {
        const { paramsValue } = get();
        set({ paramsValue: { ...paramsValue, [key]: value } }, false, 'ocr/updateParamValue');
      },

      /**
       * 设置实际请求参数值
       * @param {Object} params
       */
      setRequestParamsValue: (params) => set({ requestParamsValue: params }, false, 'ocr/setRequestParamsValue'),

      /**
       * 同时更新显示参数和请求参数
       * @param {string} key
       * @param {any} displayValue - 显示值
       * @param {any} requestValue - 请求值（可选，默认与显示值相同）
       */
      updateParams: (key, displayValue, requestValue) => {
        const { paramsValue, requestParamsValue } = get();
        set({
          paramsValue: { ...paramsValue, [key]: displayValue },
          requestParamsValue: { ...requestParamsValue, [key]: requestValue ?? displayValue }
        }, false, 'ocr/updateParams');
      },

      /**
       * 设置参数配置列表
       * @param {Array<ParamsItem>} list
       */
      setParamsList: (list) => set({ paramsList: list }, false, 'ocr/setParamsList'),

      /**
       * 重置参数到默认值
       */
      resetParams: () => {
        const { paramsList } = get();
        const defaultParams = {};
        paramsList.forEach(item => {
          defaultParams[item.key] = JSON.parse(JSON.stringify(item.default));
        });
        set({
          paramsValue: defaultParams,
          requestParamsValue: defaultParams,
          smartKeyList: []
        }, false, 'ocr/resetParams');
      },

      // ========== 智能结构化字段操作 ==========

      /**
       * 设置智能结构化字段列表
       * @param {Array<SmartKeyItem>} list
       */
      setSmartKeyList: (list) => set({ smartKeyList: list }, false, 'ocr/setSmartKeyList'),

      /**
       * 添加智能结构化字段
       * @param {SmartKeyItem} item
       */
      addSmartKey: (item) => {
        const { smartKeyList } = get();
        set({ smartKeyList: [...smartKeyList, item] }, false, 'ocr/addSmartKey');
      },

      /**
       * 更新智能结构化字段
       * @param {string} key
       * @param {Partial<SmartKeyItem>} updates
       */
      updateSmartKey: (key, updates) => {
        const { smartKeyList } = get();
        const newList = smartKeyList.map(item =>
          item.key === key ? { ...item, ...updates } : item
        );
        set({ smartKeyList: newList }, false, 'ocr/updateSmartKey');
      },

      /**
       * 删除智能结构化字段
       * @param {number} index
       */
      removeSmartKey: (index) => {
        const { smartKeyList } = get();
        const newList = [...smartKeyList];
        newList.splice(index, 1);
        set({ smartKeyList: newList }, false, 'ocr/removeSmartKey');
      },

      /**
       * 清除自定义智能字段
       */
      clearCustomSmartKeys: () => {
        const { smartKeyList } = get();
        const filtered = smartKeyList.filter(item => !item.isCustom);
        set({ smartKeyList: filtered }, false, 'ocr/clearCustomSmartKeys');
      },

      // ========== API 类型操作 ==========

      /**
       * 设置 API 类型
       * @param {string} type
       */
      setApiType: (type) => set({ apiType: type }, false, 'ocr/setApiType'),

      // ========== 请求响应操作 ==========

      /**
       * 设置请求参数
       * @param {Object} request
       */
      setRequest: (request) => set({ request }, false, 'ocr/setRequest'),

      /**
       * 设置响应数据
       * @param {Object} response
       */
      setResponse: (response) => {
        const finalResponse = { ...response };
        delete finalResponse.auditData;
        set({ response, finalResponse }, false, 'ocr/setResponse');
      },

      /**
       * 清空请求响应
       */
      clearRequestResponse: () => set({
        request: {},
        response: {},
        finalResponse: {}
      }, false, 'ocr/clearRequestResponse'),

      // ========== Tab 操作 ==========

      /**
       * 设置响应 Tab 列表
       * @param {Array<TabItem>} tabs
       */
      setRspTabs: (tabs) => set({ rspTabs: tabs }, false, 'ocr/setRspTabs'),

      /**
       * 设置当前 Tab
       * @param {string} tabId
       */
      setCurrentTab: (tabId) => set({ currentTab: tabId }, false, 'ocr/setCurrentTab'),

      // ========== 教育相关操作 ==========

      /**
       * 设置矩形选项（eduAgent）
       * @param {Array} options
       */
      setRectOptions: (options) => set({ rectOptions: options }, false, 'ocr/setRectOptions'),

      /**
       * 设置教育批改数据
       * @param {Object} data
       */
      setEduMarkData: (data) => set({ eduMarkData: data }, false, 'ocr/setEduMarkData'),

      // ========== UI 控制 ==========

      /**
       * 切换参数面板显示
       */
      toggleShowParams: () => set(
        (state) => ({ showParams: !state.showParams }),
        false,
        'ocr/toggleShowParams'
      ),

      /**
       * 设置参数面板显示状态
       * @param {boolean} show
       */
      setShowParams: (show) => set({ showParams: show }, false, 'ocr/setShowParams'),

      // ========== 初始化和重置 ==========

      /**
       * 初始化 OCR 配置
       * @param {Object} detail - 菜单配置详情
       * @param {string} apiType - API 类型
       */
      initOcrConfig: (detail, apiType) => {
        const tabs = detail.config?.Tabs || defaultRspTabs;
        let paramsList = [];
        
        if (apiType === 'sync' && detail.syncConfig) {
          paramsList = detail.syncConfig.paramsList || [];
        } else if (apiType === 'async' && detail.asyncConfig) {
          paramsList = detail.asyncConfig.paramsList || [];
        } else {
          paramsList = detail.paramsList || [];
        }

        const defaultParams = {};
        paramsList.forEach(item => {
          defaultParams[item.key] = JSON.parse(JSON.stringify(item.default));
        });

        set({
          apiType,
          paramsList,
          paramsValue: defaultParams,
          requestParamsValue: defaultParams,
          rspTabs: tabs,
          currentTab: tabs[0]?.id || 'Result',
          smartKeyList: [],
          request: {},
          response: {},
          finalResponse: {},
          rectOptions: [],
          eduMarkData: null,
        }, false, 'ocr/initOcrConfig');
      },

      /**
       * 重置 OCR 状态
       */
      resetOcr: () => set(initialState, false, 'ocr/reset'),
    }),
    { name: 'OcrStore' }
  )
);
